import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage, useFormikContext } from "formik";
import * as Yup from "yup";
import "./AdditionalForm.css"; // Ensure this CSS file is present and includes styles for .form-grid, .form-group, etc.
import InventoryServices from "../../services/InventoryServices"; // Assuming this path is correct
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setShowMessage } from "../../redux/slices/notificationSlice"; // Assuming this path is correct

// --- Define Options for Select Boxes ---
// You might want to fetch these from an API or define them more centrally
const parsingTypeOptions = [
  { value: "AGRICULTURE", label: "AGRICULTURE" },
  { value: "COMMERCIAL", label: "COMMERCIAL" },
];

const yesNoOptions = [
  { value: "YES", label: "YES" },
  { value: "NO", label: "NO" },
];
// --- End Options ---

// Define form validation schema
const validationSchema = Yup.object({
  model: Yup.string().required("Model is required"),
  location: Yup.string().required("Location is required"),
  bilNo: Yup.string().required("BIL NO is required"),
  date: Yup.date().required("Date is required"),
  bumCod: Yup.string().required("BUM COD is required"),
  chassis: Yup.string().required("Chassis is required"),
  engine: Yup.string().required("Engine is required"),
  ndp: Yup.string().required("NDP is required"),

  // New fields
  chassis_print: Yup.string().required("Chassis print is required"),
  parsing_type: Yup.string().required("Parsing Type is required"),
  subsidy: Yup.string().required("Subsidy status is required"), // Assuming Yes/No
  aadhar_card: Yup.string().required("Aadhar Card status is required"), // Assuming Yes/No/Linked etc.
  jamin_no: Yup.string().nullable(), // Optional field
  bank_passbook: Yup.string().required("Bank Passbook status is required"), // Optional field
  retail_bill_no: Yup.string().nullable(), // Optional field
  insurance: Yup.string().required("Insurance is required"), // Assuming Yes/No or plan name
  tax: Yup.string().required("Tax is required"),
  penalty: Yup.string().required("Penalty is required"),
  total: Yup.string().required("Total is required"),
  registration_no: Yup.string().nullable(), // Optional field
  no_plate_ordered: Yup.string().required("Number Plate Ordered is required"), // Assuming Yes/No
  rc_fitment: Yup.string().required("RC Fitment is required"), // Assuming Yes/No
  remark: Yup.string().nullable(), // Optional field
});

const AdditionalForm = () => {
  const location = useLocation();
  const editData = location.state?.data || null;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const InventoryService = new InventoryServices();
  const [loading, setLoading] = useState(false); // Keep loading state if needed for async operations
  const [modelOptions, setModelOptions] = useState([]);

  // Initialize state for all fields, including new ones
  const [initialValues, setInitialValues] = useState({
    model: editData?.model || "",
    location: editData?.location || "",
    bilNo: editData?.bil_no || "",
    date: editData?.date
      ? new Date(editData.date).toISOString().split("T")[0]
      : "",
    bumCod: editData?.bum_cod || "",
    chassis: editData?.chassis || "",
    engine: editData?.engine || "",
    ndp: editData?.ndp || "",

    // New fields - use backend names if they differ (e.g., editData?.parsingType)
    chassis_print: editData?.chassis_print || "",
    parsing_type: editData?.parsing_type || "",
    subsidy: editData?.subsidy || "",
    aadhar_card: editData?.aadhar_card || "",
    jamin_no: editData?.jamin_no || "",
    bank_passbook: editData?.bank_passbook || "",
    retail_bill_no: editData?.retail_bill_no || "",
    insurance: editData?.insurance || "",
    tax: editData?.tax ?? "", // Use ?? "" for potentially null/undefined numeric fields expected as strings in input
    penalty: editData?.penalty ?? "",
    total: editData?.total ?? "",
    registration_no: editData?.registration_no || "",
    no_plate_ordered: editData?.no_plate_ordered || "",
    rc_fitment: editData?.rc_fitment || "",
    remark: editData?.remark || "",
  });

  const getModelists = async () => {
    setLoading(true);
    try {
      const response = await InventoryService.getModelists();
      if (response && response.isSuccess) {
        setModelOptions(
          response.result.map((val) => ({
            label: val.modalName,
            value: val.id,
          }))
        );
      }
    } catch (error) {
      console.error(error, "Error getting model list");
      // Handle error display to user if necessary
    } finally {
      setLoading(false);
    }
  };

  function AutoTotal() {
    const { values, setFieldValue } = useFormikContext();
    useEffect(() => {
      const { insurance = "", tax = "", penalty = "" } = values;
      if (
        /^\d+$/.test(insurance) &&
        /^\d+$/.test(tax) &&
        /^\d+$/.test(penalty)
      ) {
        const sum =
          parseInt(insurance, 10) + parseInt(tax, 10) + parseInt(penalty, 10);
        const totalStr = sum.toString();
        if (values.total !== totalStr) {
          setFieldValue("total", totalStr);
        }
      }
    }, [
      values.insurance,
      values.tax,
      values.penalty,
      values.total,
      setFieldValue,
    ]);
    return null;
  }

  useEffect(() => {
    getModelists();
    // If any other dropdowns need dynamic data, fetch them here too
  }, []); // Empty dependency array means this runs once on mount

  // Handle form submission
  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    // Convert potentially empty string numbers back to null or keep as numbers
    try {
      const payload = {
        ...values,
        deliveryId: editData.deliveryId,
      };
      const response = await InventoryService.storeCombinedDetail(payload);
      if (response && response.isSuccess) {
        dispatch(setShowMessage(response.message || "Operation successful!"));
        navigate("/delivery/data");
      }
    } catch (error) {
      console.error("Submission Error:", error);
      dispatch(
        setShowMessage("An unexpected error occurred during submission.")
      );
    } finally {
      // setSubmitting(false) is automatically handled by Formik unless you throw an error
      // resetForm(); // Only reset if needed after successful submission or specific errors
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize // Important if initialValues can change after mount (e.g., fetching editData)
    >
      {(
        { isSubmitting, errors, touched } // Destructure errors and touched for easier styling/debugging
      ) => (
        <Form className="add-record-form">
          <AutoTotal />
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4 className="text-secondary mb-0">
              {/* {editData ? "Record" : "Add Record"} */}
              Final Inspection & Delivery
            </h4>
            <button
              type="button" // Prevent form submission
              className="btn btn-outline-secondary d-flex align-items-center ms-1"
              onClick={() => navigate("/delivery/steps")} // Use navigate for consistency
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="currentColor"
                className="bi bi-arrow-left-circle me-2"
                viewBox="0 0 16 16"
              >
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                <path d="M8.354 11.354a.5.5 0 0 1-.708 0L4.5 8.207l3.146-3.147a.5.5 0 0 1 .708.708L5.707 8l2.647 2.646a.5.5 0 0 1 0 .708z" />
              </svg>
              Back
            </button>
          </div>
          {/* Use a grid layout for a responsive design */}
          {/* You might need to adjust the number of columns in your CSS (.form-grid) */}
          <h7 className="section-title">Billing Details</h7>
          <div className="form-grid">
            {/* BIL NO Field */}
            <div className="form-group">
              <label htmlFor="bilNo" className="form-label">
                BIL NO
              </label>
              <Field
                type="text"
                name="bilNo"
                id="bilNo"
                placeholder="Enter BIL NO"
                className="form-input"
              />
              <ErrorMessage
                name="bilNo"
                component="div"
                className="error-message"
              />
            </div>

            {/* Date Field */}
            <div className="form-group">
              <label htmlFor="date" className="form-label">
                DATE
              </label>
              <Field type="date" name="date" id="date" className="form-input" />
              <ErrorMessage
                name="date"
                component="div"
                className="error-message"
              />
            </div>

            {/* BUM COD Field */}
            <div className="form-group">
              <label htmlFor="bumCod" className="form-label">
                BUM COD
              </label>
              <Field
                type="text"
                name="bumCod"
                id="bumCod"
                placeholder="Enter BUM COD"
                className="form-input"
              />
              <ErrorMessage
                name="bumCod"
                component="div"
                className="error-message"
              />
            </div>

            {/* Model Dropdown as the first field */}
            <div className="form-group">
              <label htmlFor="model" className="form-label">
                MODEL
              </label>
              <Field as="select" name="model" id="model" className="form-input">
                <option value="" label="Select Model" />
                {modelOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Field>
              <ErrorMessage
                name="model"
                component="div"
                className="error-message"
              />
            </div>

            {/* Chassis Field */}
            <div className="form-group">
              <label htmlFor="chassis" className="form-label">
                CHASSIS
              </label>
              <Field
                type="text"
                name="chassis"
                id="chassis"
                placeholder="Enter Chassis Number"
                className="form-input"
              />
              <ErrorMessage
                name="chassis"
                component="div"
                className="error-message"
              />
            </div>

            {/* Engine Field */}
            <div className="form-group">
              <label htmlFor="engine" className="form-label">
                ENGINE
              </label>
              <Field
                type="text"
                name="engine"
                id="engine"
                placeholder="Enter Engine Number"
                className="form-input"
              />
              <ErrorMessage
                name="engine"
                component="div"
                className="error-message"
              />
            </div>

            {/* NDP Field */}
            <div className="form-group">
              <label htmlFor="ndp" className="form-label">
                NDP
              </label>
              <Field
                type="text"
                name="ndp"
                id="ndp"
                placeholder="Enter NDP"
                className="form-input"
              />
              <ErrorMessage
                name="ndp"
                component="div"
                className="error-message"
              />
            </div>

            {/* Location Field */}
            <div className="form-group">
              <label htmlFor="location" className="form-label">
                LOCATION
              </label>
              <Field
                type="text"
                name="location"
                id="location"
                placeholder="Enter Location"
                className="form-input"
              />
              <ErrorMessage
                name="location"
                component="div"
                className="error-message"
              />
            </div>
          </div>
          <h7 className="section-title">Additional Details</h7>
          <div className="form-grid">
            {/* --- New Fields --- */}

            {/* Chassis Print Select */}
            <div className="form-group">
              <label htmlFor="chassis_print" className="form-label">
                Chassis Print <span className="text-danger">*</span>
              </label>
              <Field
                as="select"
                name="chassis_print"
                id="chassis_print"
                className={`form-input ${
                  errors.chassis_print && touched.chassis_print
                    ? "is-invalid"
                    : ""
                }`}
              >
                <option value="" label="Select Parsing Type" />
                {yesNoOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Field>
              <ErrorMessage
                name="parsing_type"
                component="div"
                className="error-message"
              />
            </div>

            {/* Parsing Type Select */}
            <div className="form-group">
              <label htmlFor="parsing_type" className="form-label">
                Parsing Type <span className="text-danger">*</span>
              </label>
              <Field
                as="select"
                name="parsing_type"
                id="parsing_type"
                className={`form-input ${
                  errors.parsing_type && touched.parsing_type
                    ? "is-invalid"
                    : ""
                }`}
              >
                <option value="" label="Select Parsing Type" />
                {parsingTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Field>
              <ErrorMessage
                name="parsing_type"
                component="div"
                className="error-message"
              />
            </div>

            {/* Subsidy Select */}
            <div className="form-group">
              <label htmlFor="subsidy" className="form-label">
                Subsidy <span className="text-danger">*</span>
              </label>
              <Field
                as="select"
                name="subsidy"
                id="subsidy"
                className={`form-input ${
                  errors.subsidy && touched.subsidy ? "is-invalid" : ""
                }`}
              >
                <option value="" label="Select Subsidy Status" />
                {yesNoOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
                {/* Add other specific options if needed */}
              </Field>
              <ErrorMessage
                name="subsidy"
                component="div"
                className="error-message"
              />
            </div>

            {/* Aadhar Card Select */}
            <div className="form-group">
              <label htmlFor="aadhar_card" className="form-label">
                Aadhar Card <span className="text-danger">*</span>
              </label>
              <Field
                as="select"
                name="aadhar_card"
                id="aadhar_card"
                className={`form-input ${
                  errors.aadhar_card && touched.aadhar_card ? "is-invalid" : ""
                }`}
              >
                <option value="" label="Select Aadhar Status" />
                {yesNoOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Field>
              <ErrorMessage
                name="aadhar_card"
                component="div"
                className="error-message"
              />
            </div>

            {/* Jamin No Field */}
            <div className="form-group">
              <label htmlFor="jamin_no" className="form-label">
                Jamin No
              </label>
              <Field
                type="text"
                name="jamin_no"
                id="jamin_no"
                placeholder="Enter Jamin No (Optional)"
                className={`form-input ${
                  errors.jamin_no && touched.jamin_no ? "is-invalid" : ""
                }`}
              />
              <ErrorMessage
                name="jamin_no"
                component="div"
                className="error-message"
              />
            </div>

            {/* Bank Passbook Field */}
            <div className="form-group">
              <label htmlFor="bank_passbook" className="form-label">
                Bank Passbook <span className="text-danger">*</span>
              </label>
              <Field
                as="select"
                name="bank_passbook"
                id="bank_passbook"
                className={`form-input ${
                  errors.bank_passbook && touched.bank_passbook
                    ? "is-invalid"
                    : ""
                }`}
              >
                <option value="" label="Select Bank Passbook Status" />
                {yesNoOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Field>
              <ErrorMessage
                name="bank_passbook"
                component="div"
                className="error-message"
              />
            </div>

            {/* Retail Bill No Field */}
            <div className="form-group">
              <label htmlFor="retail_bill_no" className="form-label">
                Retail Bill No
              </label>
              <Field
                type="text"
                name="retail_bill_no"
                id="retail_bill_no"
                placeholder="Enter Retail Bill No (Optional)"
                className={`form-input ${
                  errors.retail_bill_no && touched.retail_bill_no
                    ? "is-invalid"
                    : ""
                }`}
              />
              <ErrorMessage
                name="retail_bill_no"
                component="div"
                className="error-message"
              />
            </div>

            {/* Insurance Select/Text */}
            <div className="form-group">
              <label htmlFor="insurance" className="form-label">
                Insurance
              </label>
              <Field
                type="text"
                step="any"
                name="insurance"
                id="insurance"
                placeholder="Enter Insurance"
                className={`form-input ${
                  errors.insurance && touched.insurance ? "is-invalid" : ""
                }`}
              />
              <ErrorMessage
                name="insurance"
                component="div"
                className="error-message"
              />
            </div>

            {/* Tax Field (Number) */}
            <div className="form-group">
              <label htmlFor="tax" className="form-label">
                Tax
              </label>
              <Field
                type="text"
                step="any"
                name="tax"
                id="tax"
                placeholder="Enter Tax"
                className={`form-input ${
                  errors.tax && touched.tax ? "is-invalid" : ""
                }`}
              />
              <ErrorMessage
                name="tax"
                component="div"
                className="error-message"
              />
            </div>

            {/* Penalty Field (Number) */}
            <div className="form-group">
              <label htmlFor="penalty" className="form-label">
                Penalty
              </label>
              <Field
                type="text"
                step="any"
                name="penalty"
                id="penalty"
                placeholder="Enter Penalty"
                className={`form-input ${
                  errors.penalty && touched.penalty ? "is-invalid" : ""
                }`}
              />
              <ErrorMessage
                name="penalty"
                component="div"
                className="error-message"
              />
            </div>

            {/* Total Field (Number) */}
            <div className="form-group">
              <label htmlFor="total" className="form-label">
                Total <span className="text-danger">*</span>
              </label>
              <Field
                type="text"
                step="any"
                name="total"
                id="total"
                placeholder="Enter Total Amount"
                className={`form-input ${
                  errors.total && touched.total ? "is-invalid" : ""
                }`}
              />
              <ErrorMessage
                name="total"
                component="div"
                className="error-message"
              />
            </div>

            {/* Registration No Field */}
            <div className="form-group">
              <label htmlFor="registration_no" className="form-label">
                Registration No
              </label>
              <Field
                type="text"
                name="registration_no"
                id="registration_no"
                placeholder="Enter Registration No (Optional)"
                className={`form-input ${
                  errors.registration_no && touched.registration_no
                    ? "is-invalid"
                    : ""
                }`}
              />
              <ErrorMessage
                name="registration_no"
                component="div"
                className="error-message"
              />
            </div>

            {/* No Plate Ordered Select */}
            <div className="form-group">
              <label htmlFor="no_plate_ordered" className="form-label">
                Number Plate Ordered <span className="text-danger">*</span>
              </label>
              <Field
                as="select"
                name="no_plate_ordered"
                id="no_plate_ordered"
                className={`form-input ${
                  errors.no_plate_ordered && touched.no_plate_ordered
                    ? "is-invalid"
                    : ""
                }`}
              >
                <option value="" label="Select Status" />
                {yesNoOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Field>
              <ErrorMessage
                name="no_plate_ordered"
                component="div"
                className="error-message"
              />
            </div>

            {/* RC Fitment Select */}
            <div className="form-group">
              <label htmlFor="rc_fitment" className="form-label">
                RC Fitment <span className="text-danger">*</span>
              </label>
              <Field
                as="select"
                name="rc_fitment"
                id="rc_fitment"
                className={`form-input ${
                  errors.rc_fitment && touched.rc_fitment ? "is-invalid" : ""
                }`}
              >
                <option value="" label="Select Status" />
                {yesNoOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Field>
              <ErrorMessage
                name="rc_fitment"
                component="div"
                className="error-message"
              />
            </div>

            {/* Remark Field (Textarea) */}
            {/* Place it outside the main grid or give it more columns if needed */}
            <div className="form-group" style={{ gridColumn: "span 2" }}>
              {" "}
              {/* Example: Spanning 2 grid columns */}
              <label htmlFor="remark" className="form-label">
                Remark
              </label>
              <Field
                as="textarea"
                name="remark"
                id="remark"
                placeholder="Enter Remarks (Optional)"
                rows="2"
                className={`form-input ${
                  errors.remark && touched.remark ? "is-invalid" : ""
                }`}
              />
              <ErrorMessage
                name="remark"
                component="div"
                className="error-message"
              />
            </div>
          </div>{" "}
          {/* End of form-grid */}
          <div className="form-actions d-flex justify-content-start mt-4">
            <button
              type="submit"
              disabled={isSubmitting || loading} // Disable if submitting or loading data
              className="btn btn-primary btn-submit d-flex align-items-center" // Use Bootstrap classes
            >
              {isSubmitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Submitting...
                </>
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    fill="currentColor"
                    className="bi bi-check-circle me-2" // Changed icon
                    viewBox="0 0 16 16"
                  >
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                    <path d="M10.97 4.97a.235.235 0 0 0-.02.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05z" />
                  </svg>
                  {editData ? "Update Record" : "Submit Record"}
                  {/* Submit Record */}
                </>
              )}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default AdditionalForm;
